import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminGetUsers, adminGetStats, adminActivateUser, adminDeactivateUser, adminGrantFreeAccess, adminRevokeFreeAccess, adminDeleteUser, adminResetPassword } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Users, TrendingUp, FileText, Receipt, CheckCircle, XCircle, Clock, AlertTriangle, Loader2, Shield, Gift, Sparkles, Trash2, Ban, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const AdminPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activateDialog, setActivateDialog] = useState({ open: false, user: null });
  const [freeDialog, setFreeDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('starter');
  const [freePlan, setFreePlan] = useState('enterprise');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([adminGetUsers(), adminGetStats()]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!activateDialog.user) return;
    setActionLoading(true);
    try {
      await adminActivateUser(activateDialog.user.id, subscriptionType);
      toast.success(`Compte activé avec abonnement ${subscriptionType}`);
      setActivateDialog({ open: false, user: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'activation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Êtes-vous sûr de vouloir désactiver ce compte ?')) return;
    try {
      await adminDeactivateUser(userId);
      toast.success('Compte désactivé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };

  const handleGrantFree = async () => {
    if (!freeDialog.user) return;
    setActionLoading(true);
    try {
      await adminGrantFreeAccess(freeDialog.user.id, freePlan);
      toast.success(`Acces gratuit (${freePlan}) accorde !`);
      setFreeDialog({ open: false, user: null });
      loadData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeFree = async (userId) => {
    if (!confirm('Revoquer l\'acces gratuit ? L\'utilisateur reviendra en mode essai.')) return;
    try {
      await adminRevokeFreeAccess(userId);
      toast.success('Acces gratuit revoque');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    setActionLoading(true);
    try {
      await adminDeleteUser(deleteDialog.user.id);
      toast.success(`Compte ${deleteDialog.user.email} supprime`);
      setDeleteDialog({ open: false, user: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordDialog.user || !newPassword.trim()) return;
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setActionLoading(true);
    try {
      await adminResetPassword(passwordDialog.user.id, newPassword);
      toast.success(`Mot de passe réinitialisé pour ${passwordDialog.user.email}`);
      setPasswordDialog({ open: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status, u) => {
    if (u?.is_free_account) {
      return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100"><Gift className="w-3 h-3 mr-1" />Gratuit</Badge>;
    }
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" />Actif</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100"><Clock className="w-3 h-3 mr-1" />Essai</Badge>;
      case 'expired':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><AlertTriangle className="w-3 h-3 mr-1" />Expire</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" />Suspendu</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getSubscriptionLabel = (type, u) => {
    if (u?.is_free_account) return `Gratuit (${u.subscription_plan || 'enterprise'})`;
    switch (type) {
      case 'starter': return 'Starter (19 CHF/mois)';
      case 'pro': return 'Pro (39 CHF/mois)';
      case 'enterprise': return 'Tout-en-un (349 CHF/an)';
      case 'free': return 'Gratuit';
      default: return type || '-';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900">Accès refusé</h2>
          <p className="text-slate-500">Cette page est réservée aux administrateurs.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">Administration</h1>
        <p className="text-slate-500 mt-1">Gérez les utilisateurs et les abonnements</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                  <p className="text-xs text-slate-500">Utilisateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.active_users}</p>
                  <p className="text-xs text-slate-500">Actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_revenue} CHF</p>
                  <p className="text-xs text-slate-500">Revenus</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_invoices_platform}</p>
                  <p className="text-xs text-slate-500">Factures créées</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Utilisateurs</CardTitle>
          <CardDescription>Liste de tous les comptes clients</CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Aucun utilisateur inscrit pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Entreprise</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Statut</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Abonnement</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Usage</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Inscrit le</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{u.company_name}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-slate-600">{u.email}</p>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(u.status, u)}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-slate-600">
                          {u.subscription_type ? getSubscriptionLabel(u.subscription_type, u) : '-'}
                        </p>
                        {u.subscription_expires && (
                          <p className="text-xs text-slate-400">
                            Expire: {u.subscription_expires.slice(0, 10)}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-3 text-xs">
                          <span className="text-slate-600">
                            <Receipt className="w-3 h-3 inline mr-1" />{u.total_invoices} fact.
                          </span>
                          <span className="text-slate-600">
                            <FileText className="w-3 h-3 inline mr-1" />{u.total_quotes} devis
                          </span>
                        </div>
                        {u.status === 'trial' && u.trial_expires && (
                          <p className="text-xs text-blue-600 mt-1">
                            Essai Pro (expire: {u.trial_expires?.slice(0, 10)})
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-slate-600">{u.created_at.slice(0, 10)}</p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end flex-wrap">
                          {u.role !== 'admin' && !u.is_free_account && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-purple-300 text-purple-700 hover:bg-purple-50"
                              onClick={() => { setFreePlan('enterprise'); setFreeDialog({ open: true, user: u }); }}
                              data-testid={`grant-free-${u.id}`}
                            >
                              <Gift className="w-3 h-3 mr-1" />Offrir
                            </Button>
                          )}
                          {u.role !== 'admin' && u.is_free_account && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleRevokeFree(u.id)}
                              data-testid={`revoke-free-${u.id}`}
                            >
                              Revoquer
                            </Button>
                          )}
                          {u.status !== 'active' && !u.is_free_account && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => setActivateDialog({ open: true, user: u })}
                              data-testid={`activate-${u.id}`}
                            >
                              Activer
                            </Button>
                          )}
                          {u.role !== 'admin' && u.status !== 'suspended' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => { setNewPassword(''); setPasswordDialog({ open: true, user: u }); }}
                              data-testid={`reset-pwd-${u.id}`}
                            >
                              <KeyRound className="w-3 h-3 mr-1" />MdP
                            </Button>
                          )}
                          {u.role !== 'admin' && u.status !== 'suspended' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => handleDeactivate(u.id)}
                              data-testid={`block-${u.id}`}
                            >
                              <Ban className="w-3 h-3 mr-1" />Bloquer
                            </Button>
                          )}
                          {u.role !== 'admin' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => setDeleteDialog({ open: true, user: u })}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />Supprimer
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activate Dialog */}
      <Dialog open={activateDialog.open} onOpenChange={(open) => setActivateDialog({ open, user: activateDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activer le compte</DialogTitle>
            <DialogDescription>
              Selectionnez le type d'abonnement pour {activateDialog.user?.company_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={subscriptionType} onValueChange={setSubscriptionType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Starter - 19 CHF/mois</SelectItem>
                <SelectItem value="pro">Pro - 39 CHF/mois</SelectItem>
                <SelectItem value="enterprise">Tout-en-un - 349 CHF/an</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateDialog({ open: false, user: null })}>
              Annuler
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleActivate} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Activer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Free Access Dialog */}
      <Dialog open={freeDialog.open} onOpenChange={(open) => setFreeDialog({ open, user: freeDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              Offrir un acces gratuit
            </DialogTitle>
            <DialogDescription>
              Accorder un acces gratuit permanent a <strong>{freeDialog.user?.company_name}</strong> ({freeDialog.user?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-600">Choisissez le plan a offrir :</p>
            <Select value={freePlan} onValueChange={setFreePlan}>
              <SelectTrigger data-testid="free-plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Starter (Facturation)</SelectItem>
                <SelectItem value="pro">Pro (+ RH, Paie, Shopify)</SelectItem>
                <SelectItem value="enterprise">Tout-en-un (Tous les modules)</SelectItem>
              </SelectContent>
            </Select>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700">
                <Sparkles className="w-3 h-3 inline mr-1" />
                L'utilisateur aura un acces illimite et gratuit au plan selectionne. Vous pourrez revoquer cet acces a tout moment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreeDialog({ open: false, user: null })}>
              Annuler
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleGrantFree} disabled={actionLoading} data-testid="confirm-grant-free-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
              Offrir le plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: deleteDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer le compte
            </DialogTitle>
            <DialogDescription>
              Etes-vous sur de vouloir supprimer definitivement le compte de <strong>{deleteDialog.user?.company_name}</strong> ({deleteDialog.user?.email}) ?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs text-red-700">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Cette action est irreversible. Toutes les donnees de l'utilisateur seront supprimees : factures, devis, clients, produits, projets, depenses et entrees de temps.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>
              Annuler
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleDeleteUser} disabled={actionLoading} data-testid="confirm-delete-user-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer definitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => { setPasswordDialog({ open, user: passwordDialog.user }); if (!open) setNewPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              Réinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription>
              Définir un nouveau mot de passe pour <strong>{passwordDialog.user?.company_name}</strong> ({passwordDialog.user?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
                data-testid="new-password-input"
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700">
                Communiquez le nouveau mot de passe à l'utilisateur de manière sécurisée. Il pourra ensuite se connecter avec ce mot de passe.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPasswordDialog({ open: false, user: null }); setNewPassword(''); }}>
              Annuler
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleResetPassword} disabled={actionLoading || newPassword.length < 6} data-testid="confirm-reset-pwd-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
